import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { updateGridAndSystemProtectionSettings } from '../api/update-grid-and-system-protection-settings.api'
import { FUNCTION_NUMBER } from '../types/common'
import { GridAndSystemProtectionForm } from '../types/schemas'
import { getGridAndSystemProtectionQueryKey } from './use-grid-and-system-protection-query'
import { useDeviceLinksSuspense } from 'src/Libs/installations/hooks'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

type UseGridAndSystemProtectionMutation = {
  updateGridAndSystemProtection: (payload: Partial<GridAndSystemProtectionForm>, functionNumber: FUNCTION_NUMBER) => Promise<null>
  isLoading?: boolean
  isError?: boolean
}

const useGridAndSystemProtectionMutation = (): UseGridAndSystemProtectionMutation => {
  const { installationId } = useParams<{ installationId: string }>()
  const { deviceLinks } = useDeviceLinksSuspense(installationId)
  const emcStorageId = deviceLinks?.find(link => link.deviceType === 'emc')?.deviceId
  const storageIds = deviceLinks?.filter(link => link.deviceType === 'storage').map(link => link.deviceId) ?? []
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const { mutateAsync, isPending, isError } = usePortalMutation({
    mutationFn: async ({ payload, functionNumber }: { payload: Partial<GridAndSystemProtectionForm>; functionNumber: FUNCTION_NUMBER }) =>
      new ApiResult(await updateGridAndSystemProtectionSettings(emcStorageId, functionNumber, authFetch, payload)),
    onSuccess: (_, { functionNumber }) => {
      enqueueSuccessStack(t('systemSettings.grid-and-system-protection-configuration.api.update-success-message', { functionNumber }))
      storageIds.forEach(storageId => {
        queryClient.invalidateQueries({ queryKey: getGridAndSystemProtectionQueryKey(storageId) })
      })
    },
    onError: (error, { functionNumber }) => {
      console.error(`Error updating grid and system protection settings function: ${functionNumber} :`, error)
      enqueueErrorStack(t('systemSettings.grid-and-system-protection-configuration.api.update-error-message', { functionNumber }))
    },
  })

  const updateGridAndSystemProtection = useCallback(
    (payload: Partial<GridAndSystemProtectionForm>, functionNumber: FUNCTION_NUMBER): Promise<null> => {
      return mutateAsync({ payload, functionNumber })
    },
    [mutateAsync]
  )

  return { updateGridAndSystemProtection, isLoading: isPending, isError }
}

export default useGridAndSystemProtectionMutation
